import { useRef, useState } from "react";
import { useSearchParams, useOutletContext } from "react-router";
import type { AppOutletContext } from "./root-layout";
import { apiFetch } from "@/api";
import type { OrderCountsResponse, OrdersResponse } from "@/types";
import { useStreamedLoad } from "@/hooks/use-streamed-load";
import { LogisticsMapView, type LogisticsMapViewHandle } from "@/features/map/logistics-map-view";
import { MapLoadingOverlay } from "@/features/map/map-loading-overlay";
import { MapEnvOverlay } from "@/features/map/map-env-overlay";
import { DateCountPicker } from "@/features/routes/date-count-picker";
import { OrderSidebar } from "@/features/orders/order-sidebar";

function todayLocalIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function OrdersPage() {
  const { sites } = useOutletContext<AppOutletContext>();
  const [searchParams, setSearchParams] = useSearchParams();
  const date = searchParams.get("date") ?? todayLocalIso();
  const mapRef = useRef<LogisticsMapViewHandle>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  // Pinned orders (by order_id, in pin order) — OrderOverlay is the source of truth
  // and pushes changes here via onOrderPinnedChange (rc1.26); shared with the sidebar.
  const [pinnedOrderIds, setPinnedOrderIds] = useState<readonly string[]>([]);

  const siteParam = sites.selectedSite ? `&site=${encodeURIComponent(sites.selectedSite)}` : "";
  // Only fetch once a real env is selected — otherwise MapEnvOverlay prompts for one.
  const streamUrl =
    sites.status === "ready" && sites.selectedSiteInfo
      ? `/api/orders/stream?date=${encodeURIComponent(date)}${siteParam}`
      : null;
  const { data, loading, progress, error } = useStreamedLoad<OrdersResponse>(streamUrl);
  const orders = data?.orders ?? [];

  const fetchCounts = async (from: string, to: string): Promise<Record<string, number>> => {
    const resp = await apiFetch(`/api/order-counts?from=${from}&to=${to}${siteParam}`);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return ((await resp.json()) as OrderCountsResponse).counts;
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
      <div className="flex items-center gap-2">
        <DateCountPicker
          date={date}
          onChange={(d) =>
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set("date", d);
                return next;
              },
              { replace: true },
            )
          }
          fetchCounts={fetchCounts}
          cacheKey={`orders-${sites.selectedSite}`}
          noun="order"
        />
        <span className="text-sm text-muted-foreground">
          {error
            ? `Error: ${error}`
            : data
              ? `${orders.length} order${orders.length === 1 ? "" : "s"} · ${data.unit}`
              : (sites.selectedSiteInfo?.name ?? "")}
        </span>
      </div>
      <div className="flex min-h-[720px] flex-1 overflow-hidden rounded-lg border">
        <OrderSidebar
          orders={orders}
          hoveredIndex={hoveredIndex}
          mapRef={mapRef}
          capacityUnit={data?.unit ?? "m3"}
          pinnedOrderIds={pinnedOrderIds}
        />
        <div className="relative min-h-0 flex-1">
          <LogisticsMapView
            ref={mapRef}
            mode="orders"
            orders={orders}
            capacityUnit={data?.unit ?? "m3"}
            onHover={setHoveredIndex}
            onOrderPinnedChange={(ids) => setPinnedOrderIds(ids)}
          />
          <MapLoadingOverlay loading={loading} progress={progress} />
          {sites.status === "ready" && !sites.selectedSiteInfo && <MapEnvOverlay />}
        </div>
      </div>
    </div>
  );
}
